import { DeliveryTypeCategory, PaymentType } from '@/features/orders/constants/app'
import { useCartKeyContext } from '@/features/pos/components/Cart/contexts/CartKey'
import { useIsCartInFlightRequests } from '@/features/pos/components/Cart/hooks/cart-optimistic-update-counter'
import { useCartCache } from '@/features/pos/components/Cart/hooks/useCartCache'
import {
    useFormAutomaticContainerRemovalsValue,
    useFormCustomerAddressValue,
    useFormDeliveryTypeCategoryValue,
    useFormGetValues,
    useFormIsDefaultValuesInitialized,
} from '@/features/pos/components/OrderFinalization/form'
import {
    AutomaticContainerRemovalsInput,
    PhoneFormInput,
} from '@/features/pos/components/OrderFinalization/form/types'
import { usePhoneOrderDraft } from '@/features/pos/components/OrderFinalization/hooks/usePhoneOrderDraft'
import { usePosOrderDraft } from '@/features/pos/components/OrderFinalization/hooks/usePosOrderDraft'
import type { AutomaticContainerSetting } from '@/features/pos/components/OrderFinalization/hooks/useShouldAttachAutomaticContainers'
import {
    transformFormToPhoneOrderDraftCreateInput,
    transformFormToPosOrderDraftCreateInput,
} from '@/features/pos/components/OrderFinalization/utils/transforms'
import { usePosMode } from '@/features/pos/contexts/PosMode'
import { useEffect, useMemo } from 'react'

const useWatchFormValues = (): [
    DeliveryTypeCategory,
    string | undefined,
    AutomaticContainerRemovalsInput,
] => {
    const deliveryTypeCategory = useFormDeliveryTypeCategoryValue()
    const customerAddress = useFormCustomerAddressValue()
    const automaticContainerRemovals = useFormAutomaticContainerRemovalsValue()

    return useMemo(
        () => [deliveryTypeCategory, customerAddress?.address, automaticContainerRemovals],
        [automaticContainerRemovals, deliveryTypeCategory, customerAddress?.address]
    )
}

// since changing the payment type does not affect the totals, but it is required for order draft creation, we use defaultPaymentType as a fallback
export const useReactiveOrderDraft = (
    defaultPaymentType: PaymentType | undefined,
    automaticContainerSetting: AutomaticContainerSetting
): boolean => {
    const mode = usePosMode()

    const { cartKey } = useCartKeyContext()

    const cart = useCartCache()

    const isCartInFlightRequests = useIsCartInFlightRequests()

    const isDefaultValuesInitialized = useFormIsDefaultValuesInitialized()

    const watchedValues = useWatchFormValues()

    const getValues = useFormGetValues()

    const [posOrderDraft, posLoading] = usePosOrderDraft()
    const [phoneOrderDraft, phoneLoading] = usePhoneOrderDraft()

    useEffect(() => {
        if (!cartKey || (!cart?.items.length && !cart?.containerItems.length)) return
        if (
            isCartInFlightRequests ||
            !isDefaultValuesInitialized ||
            !automaticContainerSetting.ready
        ) {
            return
        }
        if (!defaultPaymentType) return

        if (mode === 'pos') {
            const formValues = getValues()

            posOrderDraft(
                cartKey,
                transformFormToPosOrderDraftCreateInput(
                    formValues,
                    defaultPaymentType,
                    automaticContainerSetting.shouldAttach
                )
            )

            return
        }

        const formValues = getValues() as PhoneFormInput

        phoneOrderDraft(
            cartKey,
            transformFormToPhoneOrderDraftCreateInput(
                formValues,
                defaultPaymentType,
                automaticContainerSetting.shouldAttach
            )
        )
    }, [
        mode,
        cartKey,
        cart,
        isCartInFlightRequests,
        isDefaultValuesInitialized,
        automaticContainerSetting.ready,
        automaticContainerSetting.shouldAttach,
        watchedValues,
        posOrderDraft,
        phoneOrderDraft,
        getValues,
        defaultPaymentType,
    ])

    return posLoading || phoneLoading
}
